import {
  type Connection,
  PublicKey,
  SystemProgram,
  type TransactionInstruction,
} from '@solana/web3.js';
import BN from 'bn.js';
import {
  CP_SWAP_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { invokeMethod } from './common.js';

export interface MarketConfigCommonParameters {
  configNonce: bigint;
  platformTreasury: PublicKey;
  minOutcomes: number;
  maxOutcomes: number;
  initialVirtualQuote: bigint;
  initialTokenSupply: bigint;
  minPrizePoolQuote: bigint;
  minBackAmountQuote: bigint;
  minQuoteInPerTrade: bigint;
  backerShareBps: number;
  platformShareBps: number;
  feeBpsBacking: number;
  feeBpsUnbacking: number;
  feeBpsBuy: number;
  feeBpsSell: number;
  feeBpsSellPenaltyExtra: number;
  feeBpsPrizePool: number;
  convictionBaselineReserveQuote: bigint;
  convictionReferenceMarginPpb: bigint;
  unclaimedPrizeClawbackDelaySecs: bigint;
  unclaimedBackerFeeClawbackDelaySlots: bigint;
}

export interface ConvictionConfigParameters {
  convictionResolutionThresholdPpb: bigint;
  resolutionThresholdFloorMarginPpb: bigint;
  resolutionThresholdDecayDelaySecs: bigint;
  resolutionThresholdDropIntervalSecs: bigint;
  resolutionThresholdDropPpb: bigint;
}

export type InitMarketConfigParameters = {
  payer: PublicKey;
  configAdmin: PublicKey;
  quoteMint: PublicKey;
  common: MarketConfigCommonParameters;
  convictionRetentionPerSecondPpb: bigint;
  programId?: PublicKey;
} & ({ mode: 'timed' } | { mode: 'conviction'; conviction: ConvictionConfigParameters });

export function deriveMarketConfigAddress(
  mode: 'timed' | 'conviction',
  nonce: bigint,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): PublicKey {
  const bytes = Buffer.alloc(8);
  bytes.writeBigUInt64LE(nonce);
  return PublicKey.findProgramAddressSync(
    [Buffer.from(`${mode}_market_config`), programId.toBuffer(), bytes],
    programId,
  )[0];
}

/** Both typed presets create their eight fee shards in the same instruction. */
export async function buildInitMarketConfigInstruction(
  connection: Connection,
  params: InitMarketConfigParameters,
): Promise<{
  instruction: TransactionInstruction;
  marketConfig: PublicKey;
  platformFeeVaults: PublicKey[];
}> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const marketConfig = deriveMarketConfigAddress(params.mode, params.common.configNonce, programId);
  const platformFeeVaults = Array.from(
    { length: 8 },
    (_, shard) =>
      PublicKey.findProgramAddressSync(
        [Buffer.from('platform_fee_vault'), marketConfig.toBuffer(), Buffer.from([shard])],
        programId,
      )[0],
  );
  const common = Object.fromEntries(
    Object.entries(params.common).map(([key, value]) => [
      key,
      typeof value === 'bigint' ? new BN(value.toString()) : value,
    ]),
  );
  const conviction =
    params.mode === 'conviction'
      ? Object.fromEntries(
          Object.entries(params.conviction).map(([key, value]) => [key, new BN(value.toString())]),
        )
      : {};
  const loader = new PublicKey('BPFLoaderUpgradeab1e11111111111111111111111');
  const instruction = await invokeMethod(
    program,
    params.mode === 'timed' ? 'initTimedMarketConfig' : 'initConvictionMarketConfig',
    {
      common,
      convictionRetentionPerSecondPpb: new BN(params.convictionRetentionPerSecondPpb.toString()),
      ...conviction,
    },
  )
    .accounts({
      payer: params.payer,
      configAdmin: params.configAdmin,
      adminConfig: PublicKey.findProgramAddressSync([Buffer.from('admin_config')], programId)[0],
      marketConfig,
      quoteMint: params.quoteMint,
      ...Object.fromEntries(
        platformFeeVaults.map((address, shard) => [
          shard === 0 ? 'platformFeeVault' : `platformFeeVault${shard}`,
          address,
        ]),
      ),
      morfiAmmConfig: PublicKey.findProgramAddressSync(
        [Buffer.from('amm_config'), Buffer.from([0, 1])],
        CP_SWAP_PROGRAM_ID,
      )[0],
      systemProgram: SystemProgram.programId,
      tokenProgram: TOKEN_PROGRAM_ID,
      programData: PublicKey.findProgramAddressSync([programId.toBuffer()], loader)[0],
      eventAuthority: PublicKey.findProgramAddressSync(
        [Buffer.from('__event_authority')],
        programId,
      )[0],
      program: programId,
    })
    .instruction();
  return { instruction, marketConfig, platformFeeVaults };
}

/** Availability only: economic settings and existing market snapshots are unchanged. */
export async function buildSetMarketConfigEnabledInstruction(
  connection: Connection,
  params: {
    configAdmin: PublicKey;
    marketConfig: PublicKey;
    enabled: boolean;
    programId?: PublicKey;
  },
): Promise<TransactionInstruction> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  return invokeMethod(program, 'setMarketConfigEnabled', params.enabled)
    .accounts({
      configAdmin: params.configAdmin,
      adminConfig: PublicKey.findProgramAddressSync([Buffer.from('admin_config')], programId)[0],
      marketConfig: params.marketConfig,
      eventAuthority: PublicKey.findProgramAddressSync(
        [Buffer.from('__event_authority')],
        programId,
      )[0],
      program: programId,
    })
    .instruction();
}
